import { useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { api } from "@/api/apiSlice";
import { createBackgroundJob } from "@/api/background-job/createBackgroundJob";
import { getBackgroundJobList } from "@/api/background-job/getBackgroundJobList";
import { isBackgroundJobActive } from "@/api/background-job/_models/BackgroundJob";
import type { BackgroundJob, BackgroundJobStatus } from "@/api/background-job/_models/BackgroundJob";
import type { EstoreInventoryPullDto } from "@/api/dtos";
import { ReportTags } from "@/api/report/ReportTags";
import { toEstoreInventoryPullFromDto } from "@/api/report/_transformers/toEstoreInventoryPullFromDto";
import { formatDateTime } from "@/app/date.utils";
import { useAppDispatch } from "@/app/hooks";
import { toErrorText } from "@/components/snapshot/snapshot.utils";
import { useSnapshotSection } from "@/components/snapshot/snapshotSection.context";

const PULL_JOB_KIND = "pullEstoreInventory";
const PULL_JOB_ARGS = { kind: PULL_JOB_KIND, limit: 1 };
const POLL_MS = 2000;

const toInventorySnapshotEstorePullMessage = (job: BackgroundJob | undefined): string => {
  if (!job) return "Copy eStore's new reports from its SFTP";
  if (isBackgroundJobActive(job)) return "Pulling eStore's new reports…";
  if (job.status === "FAILED") return `Last pull failed: ${job.error ?? "unknown error"}`;
  const pull = toEstoreInventoryPullFromDto(job.result as EstoreInventoryPullDto);
  let text = `${pull.pulled} new file(s) pulled`;
  if (pull.pulled === 0) text = "Nothing new on the SFTP";
  if (pull.remaining > 0) text = `${text}; ${pull.remaining} more — pull again`;
  if (pull.failed > 0) text = `${text}; ${pull.failed} failed: ${pull.firstError}`;
  let by = "";
  if (job.trigger === "SCHEDULE") by = " (scheduled)";
  return `${text} — ${formatDateTime(job.finishedAt)}${by}`;
};

/**
 * The eStore stock section's "Pull now": starts the pullEstoreInventory background job, which copies
 * eStore's new reports from its SFTP into S3. Its tooltip shows the latest pull, whoever started it.
 */
export const InventorySnapshotEstorePull = () => {
  const section = useSnapshotSection();
  const disabled = section.isBuilding;
  const dispatch = useAppDispatch();
  const jobQuery = getBackgroundJobList.useQuery(PULL_JOB_ARGS);
  const job = jobQuery.data?.[0];
  const isActive = isBackgroundJobActive(job);
  getBackgroundJobList.useQuerySubscription(PULL_JOB_ARGS, { pollingInterval: isActive ? POLL_MS : 0 });
  const [startJob, startState] = createBackgroundJob.useMutation();
  const isPulling = isActive || startState.isLoading;
  const previousStatus = useRef<BackgroundJobStatus | undefined>(job?.status);

  let message = toInventorySnapshotEstorePullMessage(job);
  if (startState.error) message = `Couldn't start the pull: ${toErrorText(startState.error)}`;

  const handlePull = async () => {
    try {
      await startJob({ kind: PULL_JOB_KIND, input: {} }).unwrap();
    }
    catch {
    }
  };

  useEffect(() => {
    const before = previousStatus.current;
    previousStatus.current = job?.status;
    const wasActive = before === "QUEUED" || before === "RUNNING";
    if (!wasActive || job?.status !== "DONE") return;
    dispatch(api.util.invalidateTags([ReportTags.ReportUpload]));
  }, [dispatch, job?.status]);

  let icon = <CloudDownloadIcon fontSize="small" />;
  if (isPulling) {
    icon = (
      <CircularProgress
        color="inherit"
        size={14}
        thickness={6}
      />
    );
  }

  return (
    <Tooltip title={message}>
      <span>
        <Button
          disabled={disabled || isPulling}
          onClick={handlePull}
          size="small"
          startIcon={icon}
          variant="outlined"
        >
          Pull now
        </Button>
      </span>
    </Tooltip>
  );
};
